/**
 * Templates → Keynotes: the firm's DEMOLITION KEYNOTE list (docs/DEMO_KEYNOTES.md).
 * AlderCAD demolition plans number their keynote tags by position in this list, so the
 * order here IS the numbering on every project. Each row says which demolished items it
 * covers (fixed in code), its wording (edit, saves on leaving the box), and whether it is
 * used at all (an unused keynote leaves those items untagged).
 */
import { useEffect, useState } from 'react'
import { listDemoKeynotes, saveDemoKeynote, reorderDemoKeynotes } from '../services/demoKeynotes.js'

const FONT = 'DM Sans, sans-serif'
const MONO = 'DM Mono, monospace'

function KeynoteRow({ k, num, onSaved, onMove, isFirst, isLast, addToast }) {
  const [text, setText] = useState(k.text)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setText(k.text) }, [k.text])

  async function commit(patch) {
    setSaving(true)
    try {
      await saveDemoKeynote(k.key, patch)
      onSaved({ ...k, ...(patch.text !== undefined ? { text: patch.text } : {}), ...(patch.active !== undefined ? { active: patch.active } : {}) })
    } catch (e) {
      addToast('Save failed: ' + (e.message || 'unknown error'))
      setText(k.text)
    } finally { setSaving(false) }
  }

  const btn = {
    border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)',
    borderRadius: 4, width: 22, height: 20, fontSize: 11, cursor: 'pointer', padding: 0,
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '34px 190px 1fr 70px 52px', gap: 10, alignItems: 'start', padding: '8px 0', borderBottom: '0.5px solid var(--aj-border)', opacity: k.active ? 1 : 0.55 }}>
      <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: 'var(--aj-text)', paddingTop: 6 }}>{k.active ? num : '—'}</div>
      <div style={{ fontFamily: FONT, fontSize: 12.5, color: 'var(--aj-text)', paddingTop: 6 }}>{k.appliesTo}</div>
      <textarea
        value={text}
        rows={2}
        disabled={saving}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => { const t = text.trim(); if (t && t !== k.text) commit({ text: t }); else setText(k.text) }}
        style={{ fontFamily: FONT, fontSize: 12, padding: '5px 7px', borderRadius: 4, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', resize: 'vertical' }}
      />
      <label style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-mid)', display: 'flex', alignItems: 'center', gap: 5, paddingTop: 6 }}
        title="Unchecked: items of this kind get no keynote tag">
        <input type="checkbox" checked={k.active} disabled={saving} onChange={(e) => commit({ active: e.target.checked })} />
        Used
      </label>
      <div style={{ display: 'flex', gap: 4, paddingTop: 4 }}>
        <button style={{ ...btn, opacity: isFirst ? 0.35 : 1 }} disabled={isFirst} onClick={() => onMove(-1)} title="Move up (changes the numbering)">↑</button>
        <button style={{ ...btn, opacity: isLast ? 0.35 : 1 }} disabled={isLast} onClick={() => onMove(1)} title="Move down (changes the numbering)">↓</button>
      </div>
    </div>
  )
}

export default function DemoKeynotesList({ addToast }) {
  const [list, setList] = useState(null)   // null = loading
  useEffect(() => { listDemoKeynotes().then(setList).catch((e) => { addToast('Could not load keynotes: ' + e.message); setList([]) }) }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  async function move(i, d) {
    const next = [...list]
    const j = i + d
    if (j < 0 || j >= next.length) return
    ;[next[i], next[j]] = [next[j], next[i]]
    setList(next)
    try { await reorderDemoKeynotes(next.map((k) => k.key)) } catch (e) { addToast('Reorder failed: ' + (e.message || 'unknown error')) }
  }

  let n = 0
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 24px 24px' }}>
      <div style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text-mid)', margin: '2px 0 12px', maxWidth: 780, lineHeight: 1.5 }}>
        Demolition keynotes. Every demolition plan tags each demolished item with the number of its keynote here, and the sheet lists the keynotes it uses. The order sets the numbers on every project. Changes show the next time a model is opened.
      </div>
      {list === null ? (
        <div style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text-mid)' }}>Loading…</div>
      ) : (
        <div style={{ maxWidth: 1000 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '34px 190px 1fr 70px 52px', gap: 10, fontFamily: MONO, fontSize: 10.5, color: 'var(--aj-text-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', paddingBottom: 6, borderBottom: '0.5px solid var(--aj-border-mid)' }}>
            <div>No.</div><div>Applies to</div><div>Keynote</div><div /><div>Order</div>
          </div>
          {list.map((k, i) => {
            if (k.active) n++
            return (
              <KeynoteRow
                key={k.key} k={k} num={n} addToast={addToast}
                isFirst={i === 0} isLast={i === list.length - 1}
                onMove={(d) => move(i, d)}
                onSaved={(nk) => setList((cur) => cur.map((x) => (x.key === nk.key ? nk : x)))}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
